import React, { useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';
import { ArchiveRounded as ArchiveIcon, UnarchiveRounded as UnarchiveIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

import { useAdminGetBasketById } from '../../hooks/admin-get-basket-by-id.hook';
import { useUpdateBasket } from '../../hooks/update-basket.hook';
import { useArchiveBasket } from '../../hooks/archive-basket.hook';
import { useRestoreBasket } from '../../hooks/restore-basket.hook';
import { UpdateBasketRequestDTO } from '../../types/dtos/update-basket.dto';
import BasketInformationsTab from '../../components/Containers/BasketInformationsTab.component';
import BasketProductsTab from '../../components/Containers/BasketProductsTab.component';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

type ArchiveAction = 'archive' | 'restore';

type DialogState = { kind: 'none' } | { kind: 'confirm'; action: ArchiveAction };

const CONFIRM_DIALOG_CONTENT: Record<ArchiveAction, { title: string; description: string; confirmLabel: string }> = {
  archive: {
    title: 'Archiver le panier',
    description: 'Le panier sera retiré des listes et du site, et pourra être restauré.',
    confirmLabel: 'Archiver',
  },
  restore: {
    title: 'Restaurer le panier',
    description: 'Le panier retrouvera sa place en fin de sous-rubrique, inactif.',
    confirmLabel: 'Restaurer',
  },
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',

    '& > h2': {
      margin: 0,
    },
  },

  archiveButton: {
    marginLeft: 'auto',
    textTransform: 'none',
  },
});

export default function AdminBasketDetailsView() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { basketId } = useParams<{ basketId: string }>();

  const params = new URLSearchParams(location.search);
  const currentTab = params.get('tab') || 'informations';

  const { isLoading, basket, refetch } = useAdminGetBasketById({ basketId });
  const { updateBasket, isSubmitting } = useUpdateBasket();
  const { archiveBasket, isSubmitting: isArchiving } = useArchiveBasket();
  const { restoreBasket, isSubmitting: isRestoring } = useRestoreBasket();

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const isArchived = Boolean(basket?.archivedAt);

  const closeDialog = () => setDialog({ kind: 'none' });

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleBasketUpdated = async (basketData: UpdateBasketRequestDTO) => {
    if (isLoading || !basket) return;

    const updatedBasket = await updateBasket({ basketId: basket.id, basketData });
    if (!updatedBasket) return;

    await refetch();
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm' || !basket) return;

    const result = dialog.action === 'archive' ? await archiveBasket(basket.id) : await restoreBasket(basket.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" height={92} />}
      {!isLoading && basket && (
        <React.Fragment>
          <div className={classes.header}>
            <h2>{basket.name}</h2>
            <StatusChip isActive={basket.isActive} archived={isArchived} />
            <Button
              size="small"
              variant="outlined"
              className={classes.archiveButton}
              startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
              onClick={() => setDialog({ kind: 'confirm', action: isArchived ? 'restore' : 'archive' })}
            >
              {isArchived ? 'Restaurer' : 'Archiver'}
            </Button>
          </div>
          <TabsController
            size="small"
            tabs={[
              { id: 'statistics', label: 'Statistiques', value: 'statistics', disabled: true },
              { id: 'informations', label: 'Informations', value: 'informations' },
              { id: 'products', label: 'Produits', value: 'products' },
            ]}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'informations' && (
            <BasketInformationsTab
              basket={basket}
              isSubmitting={isSubmitting}
              updateBasket={handleBasketUpdated}
              refreshBasket={refetch}
            />
          )}
          {currentTab === 'products' && <BasketProductsTab basket={basket} refreshBasket={refetch} />}
        </React.Fragment>
      )}
      {dialog.kind === 'confirm' && (
        <ConfirmActionDialog
          open
          title={CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={CONFIRM_DIALOG_CONTENT[dialog.action].description}
          confirmLabel={CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
